import React, { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { PATH } from 'constants/path';
import { closeManageSubscriptionModal } from 'utils/globalLayerControl';
import { setReviewGroupProviderIds } from 'utils/localstore';
import { submitWebsiteImportBatch } from 'api/group';
import s from './s.module.less';
import ChooseMethodStep, { ChooseUsageStep } from './components/ChooseMethodStep';
import InviteProvidersStep from './components/InviteProvidersStep';
import CreateProfilesStep from './components/CreateProfilesStep';
import ImportWebsiteModal from './components/ImportWebsiteModal';
import DirectUploadModal from './components/DirectUploadModal';
import WebsiteScanningPage from './components/WebsiteScanningPage';
import NoProvidersFoundPage from './components/NoProvidersFoundPage';
import ReviewProvidersPage from './components/ReviewProvidersPage';

type Props = {
    open: boolean;
    onClose: () => void;
};

type Step = 'use' | 'choose' | 'invite' | 'create';

const AddProviderModal: React.FC<Props> = ({ open, onClose }) => {
    const navigate = useNavigate();
    const [step, setStep] = useState<Step>('use');
    const [ehrOnly, setEhrOnly] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [showDirectUploadModal, setShowDirectUploadModal] = useState(false);
    const [scanningUrl, setScanningUrl] = useState<string | null>(null);
    const [showNoResults, setShowNoResults] = useState(false);
    const [showReviewProviders, setShowReviewProviders] = useState(false);
    const [batchUid, setBatchUid] = useState<string | null>(null);

    const handleClose = useCallback(() => {
        setStep('use');
        setEhrOnly(false);
        setShowImportModal(false);
        setShowDirectUploadModal(false);
        setScanningUrl(null);
        setShowNoResults(false);
        setShowReviewProviders(false);
        setBatchUid(null);
        onClose();
    }, [onClose]);

    const handleBack = useCallback(() => {
        setStep(ehrOnly ? 'use' : 'choose');
    }, [ehrOnly]);

    const handleSelectUsage = useCallback((isEhrOnly: boolean) => {
        setEhrOnly(isEhrOnly);
        setStep(isEhrOnly ? 'invite' : 'choose');
    }, []);

    const handleStartScan = useCallback((url: string, uid: string) => {
        setShowImportModal(false);
        setBatchUid(uid);
        setScanningUrl(url);
    }, []);

    const handleScanEmpty = useCallback(() => {
        setShowNoResults(true);
    }, []);

    const handleScanFound = useCallback(() => {
        if (!batchUid) return;
        const uid = batchUid;
        handleClose();
        closeManageSubscriptionModal();
        navigate(`${PATH.SELECT_WEBSITE_PROVIDERS}?batchUid=${uid}`);
    }, [batchUid, handleClose, navigate]);

    const handleUploadComplete = useCallback((uid: string) => {
        setShowDirectUploadModal(false);
        setBatchUid(uid);
        setShowReviewProviders(true);
    }, []);

    const handleRescan = useCallback(async (url: string) => {
        const websites = url
            .split(',')
            .map((u) => u.trim())
            .filter(Boolean);
        if (websites.length === 0) return;
        const res = await submitWebsiteImportBatch(websites);
        const uid: string | undefined = res?.data?.data?.batchUid;
        if (!uid) return;
        setShowNoResults(false);
        setBatchUid(uid);
        setScanningUrl(websites.join(', '));
    }, []);

    const handleExitScan = useCallback(() => {
        setScanningUrl(null);
        setShowNoResults(false);
        setBatchUid(null);
    }, []);

    const handleExitReview = useCallback(() => {
        setShowReviewProviders(false);
    }, []);

    const handleBatchConfirmed = useCallback(
        (providerIds: number[]) => {
            handleClose();
            closeManageSubscriptionModal();
            setReviewGroupProviderIds(providerIds);
            navigate(PATH.REVIEW_PROVIDER_PROFILE);
        },
        [handleClose, navigate],
    );

    const handleAddManually = useCallback(() => {
        navigate(PATH.ADD_GROUP_PROVIDER);
        closeManageSubscriptionModal();
    }, [navigate]);

    return (
        <>
            <Modal
                open={open}
                closable={false}
                footer={null}
                width={810}
                destroyOnClose
                className={s.modal}
                onCancel={handleClose}
                zIndex={99999}
            >
                <div className={s.modalContainer}>
                    <div className={s.header}>
                        <span className={s.headerTitle}>Add providers</span>
                        <span className={s.closeBtn} onClick={handleClose}>
                            <CloseOutlined />
                        </span>
                    </div>
                    {step === 'use' && <ChooseUsageStep onSelect={handleSelectUsage} />}
                    {step === 'choose' && <ChooseMethodStep onSelect={setStep} />}
                    {step === 'invite' && (
                        <InviteProvidersStep onBack={handleBack} onClose={handleClose} ehrOnly={ehrOnly} />
                    )}
                    {step === 'create' && (
                        <CreateProfilesStep
                            onBack={handleBack}
                            onClose={handleClose}
                            onSelectImport={() => setShowImportModal(true)}
                            onSelectUpload={() => setShowDirectUploadModal(true)}
                            onSelectManual={handleAddManually}
                        />
                    )}
                </div>
            </Modal>

            <ImportWebsiteModal
                open={showImportModal}
                onClose={() => setShowImportModal(false)}
                onStartScan={handleStartScan}
            />

            <DirectUploadModal
                open={showDirectUploadModal}
                onClose={() => setShowDirectUploadModal(false)}
                onUploadComplete={handleUploadComplete}
            />

            {scanningUrl &&
                batchUid &&
                createPortal(
                    <WebsiteScanningPage
                        url={scanningUrl}
                        batchUid={batchUid}
                        onExit={handleExitScan}
                        onEmpty={handleScanEmpty}
                        onFound={handleScanFound}
                    />,
                    document.body,
                )}

            {showNoResults &&
                createPortal(
                    <NoProvidersFoundPage
                        onSearch={handleRescan}
                        onExit={handleExitScan}
                        onAddManually={handleAddManually}
                    />,
                    document.body,
                )}

            {showReviewProviders &&
                createPortal(
                    <ReviewProvidersPage
                        batchUid={batchUid}
                        onExit={handleExitReview}
                        onConfirmed={handleBatchConfirmed}
                    />,
                    document.body,
                )}
        </>
    );
};

export default AddProviderModal;
